/**
 * NavigationComponents.tsx
 * Navigation components category page - Link, Navbar, Breadcrumbs, Tabs
 * 
 * Showcases all navigation-related HeroUI components with organized layout
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';
import { Breadcrumbs, BreadcrumbItem } from '@enfomentis/ui';
import { Navigation, ExternalLink, Menu, PanelTopClose, ChevronLeft } from 'lucide-react';

export const NavigationComponents: React.FC = () => {
  const navigate = useNavigate();

  const navigationComponents = [
    {
      name: 'Link',
      description: 'Navigation links with enhanced styling and accessibility',
      status: 'available',
      heroUIBase: 'Link',
      route: '/playground/components/navigation/link',
      icon: ExternalLink,
      preview: (
        <div className="space-y-2">
          <Link href="#" color="primary" size="sm">Primary Link</Link>
          <Link href="#" color="secondary" size="sm">Secondary Link</Link>
          <div className="text-text-tertiary text-center text-xs">Interactive Links</div>
        </div>
      )
    },
    {
      name: 'Navbar',
      description: 'Responsive navigation bar with mobile support',
      status: 'available',
      heroUIBase: 'Navbar',
      route: '/playground/components/navigation/navbar',
      icon: Menu,
      preview: (
        <div className="text-xs space-y-1">
          <div className="flex items-center justify-between p-2 border border-border rounded">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-primary rounded"></div>
              <span className="font-medium">Brand</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-text-secondary">Nav Item</span>
              <div className="w-4 h-2 bg-surface rounded"></div>
            </div>
          </div>
          <div className="text-text-tertiary text-center">Responsive Navigation</div>
        </div>
      )
    },
    {
      name: 'Breadcrumbs',
      description: 'Hierarchical navigation breadcrumbs with routing support',
      status: 'available',
      heroUIBase: 'Breadcrumbs',
      route: '/playground/components/navigation/breadcrumbs',
      icon: Navigation,
      preview: (
        <div className="text-xs">
          <Breadcrumbs size="sm">
            <BreadcrumbItem>Home</BreadcrumbItem>
            <BreadcrumbItem>Components</BreadcrumbItem>
            <BreadcrumbItem>Breadcrumbs</BreadcrumbItem>
          </Breadcrumbs>
          <div className="text-text-tertiary text-center mt-2">Hierarchical Navigation</div>
        </div>
      )
    },
    {
      name: 'Tabs',
      description: 'Tab navigation component with content panels',
      status: 'available',
      heroUIBase: 'Tabs',
      route: '/playground/components/navigation/tabs',
      icon: PanelTopClose,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex border-b border-border">
            <div className="px-3 py-1 border-b-2 border-primary text-primary font-medium">Tab 1</div>
            <div className="px-3 py-1 text-text-secondary">Tab 2</div>
            <div className="px-3 py-1 text-text-secondary">Tab 3</div>
          </div>
          <div className="text-text-tertiary text-center">Content Navigation</div>
        </div>
      )
    },
    {
      name: 'Pagination',
      description: 'Page navigation controls for large data sets and content',
      status: 'available',
      heroUIBase: 'Pagination',
      route: '/playground/components/navigation/pagination',
      icon: ChevronLeft,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex items-center justify-center gap-1">
            <div className="w-6 h-6 border border-border rounded flex items-center justify-center text-text-secondary">‹</div>
            <div className="w-6 h-6 bg-primary text-primary-foreground rounded flex items-center justify-center font-medium">1</div>
            <div className="w-6 h-6 border border-border rounded flex items-center justify-center text-text-secondary">2</div>
            <div className="w-6 h-6 border border-border rounded flex items-center justify-center text-text-secondary">3</div>
            <div className="w-6 h-6 border border-border rounded flex items-center justify-center text-text-secondary">›</div>
          </div>
          <div className="text-text-tertiary text-center">Data Navigation</div>
        </div>
      )
    }
  ];

  const handleComponentClick = (route: string) => {
    navigate(route);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-6">
        
        {/* Category Header */}
        <div className="text-center px-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Navigation className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Navigation Components</h1>
          </div>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Components for navigation and wayfinding throughout your application.
            Build intuitive navigation experiences with links, breadcrumbs, navbars, and tabs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <Badge color="success" variant="flat">{navigationComponents.length} components</Badge>
            <Chip size="sm" color="primary" variant="dot">HeroUI Based</Chip>
          </div>
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-4 md:px-0">
          {navigationComponents.map((component) => {
            const IconComponent = component.icon;
            
            return (
              <Card
                key={component.name}
                isPressable
                onPress={() => handleComponentClick(component.route)}
                className="group hover:shadow-lg transition-all duration-200"
              >
                <CardHeader>
                  <div className="flex items-start justify-between w-full gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <IconComponent className="w-5 h-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-base md:text-lg truncate">{component.name}</h3>
                    </div>
                    <Chip 
                      color={component.status === 'available' ? 'success' : 'default'}
                      size="sm"
                      variant="flat"
                      className="flex-shrink-0"
                    >
                      {component.status}
                    </Chip>
                  </div>
                </CardHeader>
                
                <CardBody className="space-y-4">
                  <p className="text-text-secondary">{component.description}</p>
                  
                  {/* Component Preview */}
                  <div className="p-4 bg-background rounded-lg border border-border">
                    {component.preview}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Chip size="sm" variant="bordered" color="primary">
                      {component.heroUIBase}
                    </Chip>
                  </div>
                </CardBody>
                
                <CardFooter>
                  <Button 
                    variant="bordered" 
                    color="primary"
                    onPress={() => handleComponentClick(component.route)}
                    className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                  >
                    View Examples & Documentation →
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Category Info */}
        <Card className="bg-surface">
          <CardBody>
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">About Navigation Components</h3>
              <p className="text-text-secondary max-w-3xl mx-auto">
                Navigation components help users understand where they are in your application and how to get where they want to go. 
                These components are built on HeroUI foundations with enhanced accessibility and consistent theming.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Chip size="sm" variant="flat" color="primary">Accessibility Focused</Chip>
                <Chip size="sm" variant="flat" color="secondary">Theme Integrated</Chip>
                <Chip size="sm" variant="flat" color="success">Mobile Responsive</Chip>
                <Chip size="sm" variant="flat" color="warning">Router Compatible</Chip>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};